import { Data, NeoModel, NotifyUtils } from '@singularsystems/neo-core';
import { Views } from '@singularsystems/neo-react';
import { reaction } from 'mobx';
import { NotificationDuration } from '../../../App/Models/Enums/NotificationDuration';
import { AppService, Types } from '../../../App/Services/AppService';
import { TextConstants } from '../../../Common/TextConstants';
import ManageFreeTextEventMappingComponentVM from '../Components/ManageFreeTextEventMappingComponentVM';
import CreateFreeTextMappingCommand from '../Models/Commands/CreateFreeTextMappingCommand';
import UpdateFreeTextMappingCommand from '../Models/Commands/UpdateFreeTextMappingCommand';
import FreeTextEventMappingLookup from '../Models/Lookups/FreeTextEventMappingLookup';
import FreeTextEventMappingSearchCriteria from '../Models/Queries/FreeTextEventMappingSearchCriteria';

@NeoModel
export default class FreeTextEventMappingVM extends Views.ViewModelBase {

    constructor(
        public freeTextEventMappingApiClient = AppService.get(Types.ApiClients.FreeTextEventMappingApiClient),
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        private notifications = AppService.get(Types.Neo.UI.GlobalNotifications),
        public authorisationService = AppService.get(Types.Neo.Security.AuthorisationService)) {

        super(taskRunner);
    }

    public freeTextEventMappingSearchCriteria = new FreeTextEventMappingSearchCriteria();
    public manageFreeTextMappingComponentVM = new ManageFreeTextEventMappingComponentVM();
    public showManageFreeTextMappingComponent: boolean = false;

    public pageManager = new Data.PageManager(this.freeTextEventMappingSearchCriteria, FreeTextEventMappingLookup,
        this.freeTextEventMappingApiClient.getFreeTextMappingsList, {
        pageSize: 10,
        sortAscending: true,
        fetchInitial: true,
        sortBy: "serviceProviderGroupingName",
        initialTaskRunner: this.taskRunner,
        afterFetch: this.setIsExpandedProp.bind(this),
    });

    public setIsExpandedProp(data: FreeTextEventMappingLookup[]) {
        data.forEach(fileType => {
            if (fileType.rawGenericServiceProviderEventKeywords.length > 0) {
                fileType.isExpanded = false;
            }
        })
    }

    public async initialise() {
        reaction(() => this.freeTextEventMappingSearchCriteria.searchCriteria, () => this.pageManager.refreshData(), {fireImmediately: false, delay: 650 });
    }

    public async addNewFreeTextEventMapping() {
        this.showManageFreeTextMappingComponent = true;
        await this.manageFreeTextMappingComponentVM.initialise();
        this.manageFreeTextMappingComponentVM.managedFreeTextMapping = new FreeTextEventMappingLookup();
    }

    public cancel() {
        this.manageFreeTextMappingComponentVM.managedFreeTextMapping = new FreeTextEventMappingLookup();
        this.setIndicatorsToFalse();
    }

    public async clearSearch() {
        this.freeTextEventMappingSearchCriteria.meta.searchCriteria.value = "";
        await this.pageManager.refreshData();
    }

    public async editFreeTextEventMapping(freeTextEventMapping: FreeTextEventMappingLookup) {
        this.manageFreeTextMappingComponentVM.managedFreeTextMapping.mapFrom(freeTextEventMapping).toJSObject();
        this.manageFreeTextMappingComponentVM.isFieldReadOnlyInd = true;
        await this.manageFreeTextMappingComponentVM.initialise();
        this.showManageFreeTextMappingComponent = true;
    }

    public setIndicatorsToFalse() {
        this.showManageFreeTextMappingComponent = false;
        this.manageFreeTextMappingComponentVM.isFieldReadOnlyInd = false;
    }

    public async saveFreeTextMapping() {
        this.taskRunner.run(async () => {
            var response = this.manageFreeTextMappingComponentVM.managedFreeTextMapping.rawGenericServiceProviderEventMappingId > 0
            ? await this.freeTextEventMappingApiClient.updateFreeTextMapping((this.mapFreeTextEventMappingToUpdateCommand()!.toJSObject()))
            : await this.freeTextEventMappingApiClient.createFreeTextMapping((this.mapFreeTextEventMappingToCreateCommand()!.toJSObject()));

            if (response.data.success) {
                NotifyUtils.addSuccess(TextConstants.Titles.Success, TextConstants.Notifications.FreeTextEventMappingSaved, NotificationDuration.Standard);
            }
            else {
                NotifyUtils.add(TextConstants.Titles.Error, response.data.error, "danger" as any, NotificationDuration.Long);
            }
            this.manageFreeTextMappingComponentVM.managedFreeTextMapping = new FreeTextEventMappingLookup();
            this.showManageFreeTextMappingComponent = false;
            this.setIndicatorsToFalse();
            await this.pageManager.refreshData();
        })
    }

    private mapFreeTextEventMappingToCreateCommand() {
        var command = new CreateFreeTextMappingCommand().mapFrom(this.manageFreeTextMappingComponentVM.managedFreeTextMapping);
        return command;
    }

    private mapFreeTextEventMappingToUpdateCommand() {
        var command = new UpdateFreeTextMappingCommand().mapFrom(this.manageFreeTextMappingComponentVM.managedFreeTextMapping);
        return command;
    }
}